




















































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { normalizeSearch, plural } from '@/services/browse-and-go/labels'
import { Merchant, MerchantRate, SavedSelection } from '@/services/browse-and-go/types'

interface DragPayload {
  kind: 'merchant' | 'category' | 'selection'
  id: string
}

type SourceTab = 'merchants' | 'categories' | 'selections'

@Component({
  name: 'BrowseAndGoContentComposer'
})
export default class extends Vue {
  @Prop({ required: true }) private merchants!: Merchant[]
  @Prop({ default: () => [] }) private categories!: string[]
  @Prop({ default: () => [] }) private selections!: SavedSelection[]
  @Prop({ required: true }) private selectedIds!: string[]
  @Prop({ type: Boolean, default: false }) private showCategories!: boolean
  @Prop({ type: Boolean, default: false }) private showSelections!: boolean
  @Prop({ type: Boolean, default: false }) private showRates!: boolean
  @Prop({ type: Boolean, default: false }) private excludeBank!: boolean
  @Prop({ default: () => ({}) }) private rates!: Record<string, MerchantRate>
  @Prop({ required: true }) private help!: string
  @Prop({ default: 'Rechercher' }) private searchPlaceholder!: string
  @Prop({ default: 'Éléments disponibles' }) private merchantTitle!: string
  @Prop({ default: 'Éléments sélectionnés' }) private targetTitle!: string
  @Prop({ default: 'Glissez ici une enseigne, une catégorie ou une sélection.' }) private emptyTarget!: string
  @Prop({ type: Boolean, default: false }) private bulk!: boolean

  private query = ''
  private sourceTab: SourceTab = 'merchants'
  private plural = plural
  private checkedIds: string[] = []

  private asFlag(value: boolean) {
    return value === true || (value as unknown as string) === ''
  }

  get categoriesVisible() {
    return this.asFlag(this.showCategories)
  }

  get selectionsVisible() {
    return this.asFlag(this.showSelections)
  }

  get ratesVisible() {
    return this.asFlag(this.showRates)
  }

  get bulkOn() {
    return this.asFlag(this.bulk)
  }

  get activeKind(): SourceTab {
    if (this.sourceCount > 1 && this.sourceTab === 'categories' && this.categoriesVisible) return 'categories'
    if (this.sourceCount > 1 && this.sourceTab === 'selections' && this.selectionsVisible) return 'selections'
    return 'merchants'
  }

  get currentIds() {
    if (this.activeKind === 'categories') return this.availableCategories.map(category => category.name)
    if (this.activeKind === 'selections') return this.availableSelections.map(selection => selection.id)
    return this.availableMerchants.map(merchant => merchant.id)
  }

  get allChecked() {
    return this.currentIds.length > 0 && this.currentIds.every(id => this.checkedIds.includes(id))
  }

  get someChecked() {
    const count = this.currentIds.filter(id => this.checkedIds.includes(id)).length
    return count > 0 && count < this.currentIds.length
  }

  @Watch('sourceTab')
  @Watch('query')
  private clearChecked() {
    this.checkedIds = []
  }

  get sourceCount() {
    return 1 + (this.categoriesVisible ? 1 : 0) + (this.selectionsVisible ? 1 : 0)
  }

  get selectedSet() {
    return new Set(this.selectedIds)
  }

  private matches(merchant: Merchant, needle: string) {
    if (!needle) return true
    return normalizeSearch(merchant.name).includes(needle) ||
      merchant.categories.some(category => normalizeSearch(category).includes(needle))
  }

  private eligible(merchant: Merchant) {
    return !this.asFlag(this.excludeBank) || !merchant.bank
  }

  get availableMerchants() {
    const needle = normalizeSearch(this.query)
    return this.merchants
      .filter(merchant => this.eligible(merchant) && !this.selectedSet.has(merchant.id) && this.matches(merchant, needle))
      .sort((a, b) => {
        if (a.bank !== b.bank) return a.bank ? 1 : -1
        return a.name.localeCompare(b.name, 'fr')
      })
  }

  get merchantRows() {
    const rows: Array<{ kind: 'header'; id: string; merchant?: undefined } | { kind: 'item'; id: string; merchant: Merchant }> = []
    let bankHeader = false
    this.availableMerchants.forEach(merchant => {
      if (merchant.bank && !bankHeader) {
        rows.push({ kind: 'header', id: 'bank-header' })
        bankHeader = true
      }
      rows.push({ kind: 'item', id: merchant.id, merchant })
    })
    return rows
  }

  get availableCategories() {
    const needle = normalizeSearch(this.query)
    return this.categories
      .map(name => {
        const remaining = this.merchants.filter(merchant =>
          merchant.categories.includes(name) && this.eligible(merchant) && !this.selectedSet.has(merchant.id)
        )
        return { name, count: remaining.length, remaining }
      })
      .filter(category => {
        if (!category.count) return false
        if (!needle) return true
        return normalizeSearch(category.name).includes(needle) ||
          category.remaining.some(merchant => this.matches(merchant, needle))
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  }

  get availableSelections() {
    const needle = normalizeSearch(this.query)
    return this.selections
      .map(selection => {
        const remaining = selection.itemIds
          .map(id => this.merchants.find(merchant => merchant.id === id))
          .filter((merchant): merchant is Merchant => Boolean(merchant))
          .filter(merchant => this.eligible(merchant) && !this.selectedSet.has(merchant.id))
        return { id: selection.id, name: selection.name, count: remaining.length, remaining }
      })
      .filter(selection => {
        if (!selection.count) return false
        if (!needle) return true
        return normalizeSearch(selection.name).includes(needle) ||
          selection.remaining.some(merchant => this.matches(merchant, needle))
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  }

  get selectedMerchants() {
    return this.selectedIds
      .map(id => this.merchants.find(merchant => merchant.id === id))
      .filter((merchant): merchant is Merchant => Boolean(merchant))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  }

  private subtitle(merchant: Merchant) {
    const categories = merchant.categories.join(' · ')
    if (merchant.bank) return categories ? `Carte bancaire · ${categories}` : 'Support : carte bancaire'
    return categories || 'Enseigne'
  }

  private rateOf(id: string): MerchantRate {
    return this.rates[id] || { commission: 8, discount: 5 }
  }

  private marginValue(id: string) {
    const rate = this.rateOf(id)
    return rate.commission - rate.discount
  }

  private emitIds(ids: string[]) {
    const unique = Array.from(new Set(ids))
    if (unique.length) this.$emit('add-ids', unique)
  }

  private addMerchant(id: string) {
    this.emitIds([id])
  }

  private addCategory(name: string) {
    const ids = this.merchants
      .filter(merchant => merchant.categories.includes(name) && this.eligible(merchant))
      .map(merchant => merchant.id)
    this.emitIds(ids)
  }

  private addSelection(id: string) {
    const selection = this.selections.find(item => item.id === id)
    if (!selection) return
    const ids = selection.itemIds.filter(itemId => {
      const merchant = this.merchants.find(item => item.id === itemId)
      return merchant ? this.eligible(merchant) : false
    })
    this.emitIds(ids)
  }

  private isChecked(id: string) {
    return this.checkedIds.includes(id)
  }

  private onRowCheck(id: string) {
    if (!this.bulkOn) return
    this.toggleCheck(id, !this.isChecked(id))
  }

  private toggleCheck(id: string, checked: boolean) {
    if (checked) {
      if (!this.checkedIds.includes(id)) this.checkedIds.push(id)
      return
    }
    this.checkedIds = this.checkedIds.filter(item => item !== id)
  }

  private toggleAll(checked: boolean) {
    if (checked) {
      this.checkedIds = this.currentIds.slice()
      return
    }
    this.checkedIds = []
  }

  private addChecked() {
    const ids = this.checkedIds.slice()
    this.checkedIds = []
    if (this.activeKind === 'categories') {
      ids.forEach(name => this.addCategory(name))
      return
    }
    if (this.activeKind === 'selections') {
      ids.forEach(id => this.addSelection(id))
      return
    }
    this.emitIds(ids)
  }

  private onDragStart(event: DragEvent, payload: DragPayload) {
    if (!event.dataTransfer) return
    event.dataTransfer.setData('text/plain', JSON.stringify(payload))
    event.dataTransfer.effectAllowed = 'copy'
  }

  private onDrop(event: DragEvent) {
    const raw = event.dataTransfer ? event.dataTransfer.getData('text/plain') : ''
    if (!raw) return
    try {
      const payload = JSON.parse(raw) as DragPayload
      if (payload.kind === 'merchant') this.addMerchant(payload.id)
      if (payload.kind === 'category') this.addCategory(payload.id)
      if (payload.kind === 'selection') this.addSelection(payload.id)
    } catch {
      return
    }
  }

  private onRate(id: string, key: 'commission' | 'discount', value: number | undefined) {
    this.$emit('update-rate', {
      id,
      key,
      value: typeof value === 'number' ? value : 0
    })
  }
}
